class AkTrioComposer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.form = this.closest('form');
    this.productForm = this.closest('product-form-component');
    this.inputs = [...this.querySelectorAll('[data-ak-trio-input]')];
    this.slots = [...this.querySelectorAll('[data-ak-trio-slot]')];
    this.flavours = [...this.querySelectorAll('[data-ak-trio-flavour]')];
    this.activeSlot = 1;
    this.storageKey = `alyone-trio-${this.dataset.productId || 'builder'}`;

    this.addEventListener('click', this.handleClick);
    this.form?.addEventListener('submit', this.validateSubmission, true);
    this.form?.addEventListener('click', this.guardAddToCart, true);
    this.observeVariant();
    this.restore();
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.form?.removeEventListener('submit', this.validateSubmission, true);
    this.form?.removeEventListener('click', this.guardAddToCart, true);
    this.variantObserver?.disconnect();
  }

  handleClick = (event) => {
    const slot = event.target.closest('[data-ak-trio-slot]');
    if (slot) {
      this.activeSlot = Number(slot.dataset.akTrioSlot);
      this.render();
      return;
    }

    const flavour = event.target.closest('[data-ak-trio-flavour]');
    if (flavour) {
      if (flavour.disabled) return;
      this.choose(flavour.dataset.akTrioFlavour);
      return;
    }

    if (event.target.closest('[data-ak-house-trio]')) {
      const choices = (this.dataset.houseTrio || '').split('|').filter(Boolean);
      this.inputs.forEach((input, index) => { input.value = choices[index] || ''; });
      this.activeSlot = 3;
      this.persist();
      this.render('The House Trio is ready. You can still change any flavour.');
      return;
    }

    if (event.target.closest('[data-ak-trio-clear]')) {
      this.inputs.forEach((input) => { input.value = ''; });
      this.activeSlot = 1;
      this.persist();
      this.render('Choices cleared. Choose your first flavour.');
      return;
    }

  };

  choose(value) {
    const input = this.inputs[this.activeSlot - 1];
    if (!input) return;
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));

    const nextEmpty = this.inputs.findIndex((item, index) => index >= this.activeSlot && !item.value);
    if (nextEmpty >= 0) this.activeSlot = nextEmpty + 1;
    else {
      const anyEmpty = this.inputs.findIndex((item) => !item.value);
      if (anyEmpty >= 0) this.activeSlot = anyEmpty + 1;
    }

    this.persist();
    this.render();
  }

  get selected() {
    return this.inputs.map((input) => input.value.trim());
  }

  get complete() {
    return this.selected.every(Boolean);
  }

  render(announcement = '') {
    const selected = this.selected;
    const completed = selected.filter(Boolean).length;

    this.slots.forEach((slot, index) => {
      const value = selected[index];
      const isActive = index + 1 === this.activeSlot;
      slot.classList.toggle('is-active', isActive);
      slot.classList.toggle('is-filled', Boolean(value));
      slot.querySelector('[data-ak-slot-value]').textContent = value ? this.shortName(value) : 'Choose a flavour';
      slot.querySelector('.ak-trio-slot__mark').textContent = value ? '✓' : '+';
    });

    this.flavours.forEach((button) => {
      const chosenForActiveSlot = selected[this.activeSlot - 1] === button.dataset.akTrioFlavour;
      const chosenCount = selected.filter((value) => value === button.dataset.akTrioFlavour).length;
      button.classList.toggle('is-selected', chosenForActiveSlot);
      button.classList.toggle('is-in-box', chosenCount > 0);
      button.setAttribute('aria-pressed', chosenForActiveSlot ? 'true' : 'false');
      button.dataset.chosenCount = String(chosenCount);
      const count = button.querySelector('[data-ak-flavour-count]');
      if (count) {
        count.textContent = chosenCount > 1 ? `×${chosenCount}` : '';
        count.hidden = chosenCount < 2;
      }
    });

    const progress = this.querySelector('[data-ak-progress-bar]');
    if (progress) progress.style.width = `${(completed / 3) * 100}%`;

    const count = this.querySelector('[data-ak-trio-count]');
    if (count) count.textContent = `${completed} / 3`;

    const paletteTitle = this.querySelector('[data-ak-palette-title]');
    if (paletteTitle) paletteTitle.textContent = this.complete ? `Change flavour ${this.activeSlot}` : `Choose flavour ${this.activeSlot}`;

    const status = this.querySelector('[data-ak-trio-status]');
    if (status) {
      status.textContent = announcement || (this.complete
        ? 'Your box is composed · all three flavours selected'
        : `${completed} of 3 selected · choose flavour ${this.activeSlot}`);
    }

    const summary = this.querySelector('[data-ak-trio-summary]');
    if (summary) {
      summary.textContent = this.complete
        ? 'Your tasting is composed and ready to add.'
        : `${3 - completed} ${3 - completed === 1 ? 'choice' : 'choices'} left to complete your box.`;
    }

    this.toggleAttribute('data-complete', this.complete);
    this.querySelector('[data-ak-trio-error]')?.toggleAttribute('hidden', true);
    this.updateAddToCartLabel();
  }

  updateAddToCartLabel() {
    const label = this.form?.querySelector('.add-to-cart-text__content');
    const stickyLabel = document.querySelector('ak-sticky-atc[data-product-id="' + this.dataset.productId + '"] [data-ak-sticky-label]');
    const remaining = 3 - this.selected.filter(Boolean).length;
    const text = this.complete
      ? 'Add composed box'
      : `Choose ${remaining} more ${remaining === 1 ? 'flavour' : 'flavours'}`;
    if (label) label.textContent = this.complete ? 'Add composed box to cart' : text;
    if (stickyLabel) stickyLabel.textContent = text;
  }

  guardAddToCart = (event) => {
    if (!event.target.closest('[name="add"]') || this.complete) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.showValidation();
  };

  validateSubmission = (event) => {
    if (this.complete) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.showValidation();
  };

  showValidation() {
    const firstEmpty = this.inputs.findIndex((input) => !input.value);
    this.activeSlot = firstEmpty >= 0 ? firstEmpty + 1 : 1;
    this.render('Complete all three choices to add your box.');
    const error = this.querySelector('[data-ak-trio-error]');
    if (error) error.hidden = false;
    this.slots[this.activeSlot - 1]?.focus();
    this.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
  }

  persist() {
    try { localStorage.setItem(this.storageKey, JSON.stringify(this.selected)); } catch (_) {}
  }

  restore() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
      if (!Array.isArray(saved)) return;
      const valid = new Set(this.flavours.map((button) => button.dataset.akTrioFlavour));
      this.inputs.forEach((input, index) => { input.value = valid.has(saved[index]) ? saved[index] : ''; });
      const firstEmpty = this.inputs.findIndex((input) => !input.value);
      this.activeSlot = firstEmpty >= 0 ? firstEmpty + 1 : 3;
    } catch (_) {}
  }

  shortName(value) {
    return value.replace('Signature Belgian Dark Chocolate Fudge', 'Signature Fudge').replace('Belgian Dark Chocolate ', '');
  }

  observeVariant() {
    const variantInput = this.form?.querySelector('[name="id"]');
    if (!variantInput) return;
    const update = () => {
      const selected = this.productForm?.closest('.shopify-section')?.querySelector('variant-selects [data-selected-variant]')?.textContent || '';
      const label = this.querySelector('[data-ak-size-note] strong');
      const visibleSize = document.querySelector('[data-product-id="' + this.dataset.productId + '"] input[name="id"]')?.value;
      const pickerText = this.productForm?.closest('.product-information')?.querySelector('.variant-option__button-label input:checked')?.value || selected;
      const size = String(pickerText || visibleSize || '').trim();
      if (label && !/^\d{10,}$/.test(size)) {
        const boxCount = /1\s*kg/i.test(size) ? 12 : /500/.test(size) ? 6 : 3;
        label.textContent = `${boxCount} boxes · ${boxCount * 16} pieces · three flavours`;
      }
      this.render();
    };
    this.variantObserver = new MutationObserver(update);
    this.variantObserver.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
    this.productForm?.addEventListener('change', () => requestAnimationFrame(update));
    update();
  }
}

if (!customElements.get('ak-trio-composer')) customElements.define('ak-trio-composer', AkTrioComposer);
